// Firebase Configuration for SwipeSage Admin Panel
import { initializeApp, FirebaseApp } from 'firebase/app';
import { getAuth, Auth } from 'firebase/auth';
import { getFirestore, Firestore } from 'firebase/firestore';
import { getAnalytics, Analytics } from 'firebase/analytics';
import { getStorage, FirebaseStorage } from 'firebase/storage';
import { getFunctions, Functions } from 'firebase/functions';

// Firebase config sourced from build-time env vars.
// Local dev: populate apps/admin_panel_react/.env (see .env.example).
// CI/CD:    populate GitHub repo Variables (VITE_FIREBASE_*).
// These values aren't secret (they identify the project, not authenticate)
// but env vars give us per-environment overrides and a single source of truth.
const firebaseConfig = {
  apiKey:            import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain:        import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId:         import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket:     import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId:             import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId:     import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

// Fail-fast if a required key is missing — Firebase's own error would be
// cryptic ("Invalid API key") and lands deep in the SDK.
if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
  throw new Error(
    'Missing Firebase configuration. Ensure VITE_FIREBASE_API_KEY and ' +
    'VITE_FIREBASE_PROJECT_ID are set (see .env.example).',
  );
}

// Initialize Firebase
let app: FirebaseApp;
let auth: Auth;
let firestore: Firestore;
let analytics: Analytics | null = null;
let storage: FirebaseStorage;
let functions: Functions;

// Initialize Firebase services
export const initializeFirebase = () => {
  if (!app) {
    app = initializeApp(firebaseConfig);
    auth = getAuth(app);
    firestore = getFirestore(app);
    storage = getStorage(app);
    // Callables live in us-central1 (same region as the deployed functions).
    functions = getFunctions(app, 'us-central1');

    // Analytics only works in browser environment
    if (typeof window !== 'undefined') {
      try {
        analytics = getAnalytics(app);
      } catch (error) {
        if (import.meta.env.DEV) console.warn('Analytics initialization failed:', error);
      }
    }
  }
  
  return {
    app,
    auth,
    firestore,
    analytics,
    storage,
    functions
  };
};

// Export initialized services
export const getFirebaseApp = () => app;
export const getFirebaseAuth = () => auth;
export const getFirebaseFirestore = () => firestore;
export const getFirebaseAnalytics = () => analytics;
export const getFirebaseStorage = () => storage;
export const getFirebaseFunctions = () => functions;

// Initialize on import
initializeFirebase();

// Export default configuration
export default firebaseConfig; 